import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { schoolRegistrationFormSchema } from '../components/registrationFormComponents/schema';

import { AppContext } from '../contexts/app.context';
import { Button, Input, Loader, Text } from '../components';
import {
  EstablishmentDetailsForm,
  ClassDetailsForm,
  EmailConsentForm,
  InchargePersonForm,
  TotalStudentDisplay,
} from '../components/registrationFormComponents';
import { schoolRegistration, updateDashboardDetails, updateRegistrationDetails } from '../service';
import { toast } from 'react-toastify';
import { scrollToClass, submitHelper } from '../components/registrationFormComponents/util';
import { FINAL_BOOKING_TIME } from '../components/registrationFormComponents/data';

const translationPrefix = 'schoolRegistrationForm.form';

const classInitialValues = {
  name: '',
  level: '',
  otherLevel: '',
  numberOfStudents: '',
  sector: '',
  visitDay: '',
  arrivalTime: '',
  departureTime: '',
  modeOfTransport: '',
  otherModeOfTransport: '',
  picnicOnSite: 'false',
  handicap: 'false',
  handicapDescription: '',
  responsibleInformation: {
    firstName: '',
    lastName: '',
    function: '',
    email: '',
    phone: '',
  },
  bookExperience: 'true',
  experienceDetails: {
    tourType: '',
    timeSlot: '',
    level: '',
    guidedTourType: '',
    professionId: '',
  },
};

const SchoolRegistrationForm = ({ storedData, refresh, refreshing }) => {
  const { t } = useTranslation();
  const { admin } = useContext(AppContext);
  const { setBaseContainerConfig } = useContext(AppContext);

  const { id } = useParams();

  const [saving, setSaving] = useState(false);
  const [openClassIndex, setOpenClassIndex] = useState(null);
  const [experienceInteractionConfig, setExperienceInteractionConfig] = useState({
    visible: true,
    editable: true,
    newBooking: true,
    finalBookingTimePassed: false,
  });

  const formik = useFormik({
    initialValues: {
      establishmentDetails: {
        privateContract: 'false',
        rep: 'false',
      },
      inchargePersonDetails: {},
      inchargePersonAnother: 'true',
      inchargePersonAnotherDetails: {},
      classes: [
        {
          ...classInitialValues,
          bookExperience: new Date() >= FINAL_BOOKING_TIME ? 'false' : 'true',
        },
      ],
      emailConsent: {
        emailConsent: 'true',
        email: '',
      },
    },
    validationSchema: schoolRegistrationFormSchema,
    onSubmit: async (values) => {
      try {
        const emailConsentGiven = values?.emailConsent?.emailConsent;
        const emailConsentEmail = values?.emailConsent?.email;
        const inchargePersonEmail = values?.inchargePersonDetails?.email;

        if (emailConsentGiven === 'true' && emailConsentEmail !== inchargePersonEmail) {
          toast.error(t('schoolRegistrationForm.popupError.emailConsentEmail'));
          return;
        }

        let classStudentNumberErrors = [];

        const studentsLimit = {
          introduction: { min: 10, max: 35 },
          guided: { min: 1, max: 25 },
        };

        values?.classes?.forEach((classDetail) => {
          if (classDetail?.visitDay === 'thu' || classDetail?.visitDay === 'fri') {
            if (
              Number(classDetail?.numberOfStudents) <
              studentsLimit[classDetail?.experienceDetails?.tourType]?.min
            ) {
              classStudentNumberErrors.push(
                `${classDetail?.name} ${t('schoolRegistrationForm.popupError.numberOfStudentsMin')}`,
              );
            }
            if (
              Number(classDetail?.numberOfStudents) >
              studentsLimit[classDetail?.experienceDetails?.tourType]?.max
            ) {
              classStudentNumberErrors.push(
                `${classDetail?.name} ${t('schoolRegistrationForm.popupError.numberOfStudentsMax')}`,
              );
            }
          }
        });

        if (classStudentNumberErrors.length > 0) {
          classStudentNumberErrors.forEach((error) => {
            toast.error(error);
          });
          return;
        }

        setSaving(true);
        !storedData
          ? await schoolRegistration(values)
          : id && admin
            ? await updateRegistrationDetails(id, { ...values, formType: 'school' })
            : await updateDashboardDetails({ ...values, formType: 'school' });
        toast.success(t('schoolRegistrationForm.success'));

        if (refresh) {
          refresh();
        }
      } catch (error) {
        console.log(error);
        if (error?.response?.data?.errors) {
          const errors = Object.keys(error?.response?.data?.errors);
          errors.forEach((error) => {
            toast.error(t(`${translationPrefix}.popupError.${error}`));
          });
        } else {
          toast.error(t('schoolRegistrationForm.error'));
        }
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    if (!storedData) return;

    formik.setValues(storedData);
  }, [storedData]);

  useEffect(() => {
    if (new Date() >= FINAL_BOOKING_TIME) {
      setExperienceInteractionConfig({
        visible: storedData?.classes?.length > 0,
        editable: admin?.specialPermissions?.includes('edit'),
        newBooking: storedData ? Object.keys(storedData)?.length === 0 : true,
        finalBookingTimePassed: new Date() >= FINAL_BOOKING_TIME,
      });
    }
  }, [storedData, admin]);

  console.log(experienceInteractionConfig);

  useEffect(() => {
    setBaseContainerConfig({
      heading: t('schoolRegistrationForm.header.heading'),
      subHeading: t('schoolRegistrationForm.header.subHeading'),
      buttonText: t('loginButtonText'),
      buttonURL: '/login',
    });
  }, []);

  return (
    <>
      <div className="text-center">
        <Text type={'big'} text={t('schoolRegistrationForm.heading')} />
      </div>

      <form
        onSubmit={(e) => {
          submitHelper(e, formik, t);
        }}>
        <div className="w-full mt-12">
          <Text
            type={'small'}
            text={t(`${translationPrefix}.heading`)}
            className={'bg-blue-10 text-center text-white p-2 w-full rounded-md mb-2'}
          />

          <div className="bg-lightBlue-10 rounded-lg p-4 sm:px-10 pt-10">
            <EstablishmentDetailsForm formik={formik} translationPrefix={translationPrefix} />

            <span className="block my-10 bg-black h-[0.5px] w-full" />

            <Text
              type={'small'}
              text={t(`${translationPrefix}.inchargePerson.heading`)}
              className={'text-center mb-6 font-semibold'}
            />

            <InchargePersonForm formik={formik} translationPrefix={translationPrefix} />

            <span className="block my-10 bg-black h-[0.5px] w-full" />

            <Text
              type={'small'}
              text={t(`${translationPrefix}.inchargePersonAnother.heading`)}
              className={'text-center mb-6 font-semibold'}
            />

            <Input
              name={'inchargePersonAnother'}
              id={'inchargePersonAnother'}
              paragraphLabel={t(`${translationPrefix}.inchargePersonAnother.label`)}
              paragraphLabelClassName="text-center"
              radioClassName="justify-center"
              type={'radio'}
              options={[
                {
                  value: 'false',
                  label: t(`${translationPrefix}.inchargePersonAnother.false`),
                },
                {
                  value: 'true',
                  label: t(`${translationPrefix}.inchargePersonAnother.true`),
                },
              ]}
              defaultValue={formik.values?.inchargePersonAnother || 'true'}
              onChange={(value) => {
                formik.setFieldValue('inchargePersonAnother', value);
              }}
            />

            {formik.values?.inchargePersonAnother === 'false' && (
              <>
                <span className="block my-10 w-full" />
                <InchargePersonForm
                  formik={formik}
                  objectName={'inchargePersonAnotherDetails'}
                  translationPrefix={translationPrefix}
                />
              </>
            )}
          </div>
        </div>

        <div className="w-full mt-6">
          <Text
            type={'small'}
            text={t(`${translationPrefix}.visit.heading`)}
            className={'bg-blue-10 text-center text-white p-2 w-full rounded-md mb-2'}
          />

          <div className="bg-lightBlue-10 rounded-lg p-4 sm:px-10 pt-10">
            <ClassDetailsForm
              formik={formik}
              translationPrefix={translationPrefix}
              openClassIndex={openClassIndex}
              experienceInteractionConfig={experienceInteractionConfig}
            />

            <div className="w-full flex items-center justify-center my-8">
              <Button
                type="button"
                text={t('schoolRegistrationForm.addClassButtonText')}
                className={'bg-transparent border-2 border-red-10 !text-red-10 rounded-md'}
                callback={() => {
                  formik.setFieldValue('classes', [
                    ...formik.values.classes,
                    {
                      ...classInitialValues,
                      bookExperience: new Date() >= FINAL_BOOKING_TIME ? 'false' : 'true',
                    },
                  ]);
                  setOpenClassIndex(formik.values.classes.length);
                  scrollToClass(`class-${formik.values.classes.length}`);
                }}
              />
            </div>
            <TotalStudentDisplay formik={formik} translationPrefix={translationPrefix} />
          </div>
        </div>

        <div className="w-full mt-6">
          <Text
            type={'medium'}
            text={t(`${translationPrefix}.emailConsent.heading`)}
            className={'mb-6 mt-4'}
          />

          <EmailConsentForm formik={formik} translationPrefix={translationPrefix} />

          <Button
            type="submit"
            text={t('schoolRegistrationForm.submitButtonText')}
            className={'mt-20'}
            loading={formik.isSubmitting}
          />

          <Text
            type={'medium'}
            text={t(`${translationPrefix}.support.heading`)}
            className={'mt-12'}
          />
          <Text
            type={'small'}
            text={t(`${translationPrefix}.support.paragraph`)}
            className={'mb-2'}
          />
          <Text
            type={'small'}
            text={t(`${translationPrefix}.termsAndConditions`)}
            className={'mt-6 text-red-10'}
          />
        </div>
      </form>

      {(saving || refreshing) && (
        <Loader
          bgColor={'transparent'}
          loaderType={'sync'}
          text={storedData ? t('updating') : t('saving')}
        />
      )}
    </>
  );
};

export default SchoolRegistrationForm;
